import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BillingSummary } from '@/billing/api/FetchBillingList';
import ActionButtons from '@/billing/component/ActionButtons';
import DocumentItem from '@/billing/component/DocumentItem';
import { ProcessSelect } from '@/billing/component/ProcessSelect';
import { RenameDocumentForm } from '@/billing/component/RenameDocumentForm';
import { useIsLockedByOtherUser } from '@/billing/hooks/useIsLockedByOtherUser';
import { useSelectedBillingProcess } from '@/billing/hooks/useSelectedBillingProcess';

interface DocumentsTabProps {
  billing: BillingSummary;
  onClose: () => void;
  openedDocumentId?: number;
  onToggleDocument: (attachmentId: number) => void;
  selectedProcessCode?: string;
  onSelectProcess: (processCode: string) => void;
}

export const DocumentsTab = ({
  billing,
  onClose,
  openedDocumentId,
  onToggleDocument,
  selectedProcessCode,
  onSelectProcess,
}: DocumentsTabProps) => {
  const { t } = useTranslation();
  const { process } = useSelectedBillingProcess(billing, selectedProcessCode);
  const isLocked = useIsLockedByOtherUser(billing);
  const [renamingAttachmentId, setRenamingAttachmentId] = useState<number>();

  const canRename = billing.status !== 'COMPLETED' && !isLocked && !!process;

  return (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 flex flex-col gap-4">
        {billing.status !== 'COMPLETED' && (
          <ProcessSelect
            billing={billing}
            value={selectedProcessCode}
            onValueChange={onSelectProcess}
          />
        )}

        <ul className="flex flex-col gap-3">
          {billing.attachmentIds.length ? (
            billing.attachmentIds.map((attachmentId) => (
              <DocumentItem
                key={attachmentId}
                attachmentId={attachmentId}
                openedDocumentId={openedDocumentId}
                onToggleDocument={onToggleDocument}
                canRename={canRename}
                onRename={() => setRenamingAttachmentId(attachmentId)}
              >
                {canRename && renamingAttachmentId === attachmentId && (
                  <RenameDocumentForm
                    key={process.code}
                    billing={billing}
                    process={process}
                    onCancel={() => setRenamingAttachmentId(undefined)}
                  />
                )}
              </DocumentItem>
            ))
          ) : (
            <p>{t('billing.drawer.noAttachmentsFound')}</p>
          )}
        </ul>
      </div>

      {billing.status !== 'COMPLETED' && (
        <ActionButtons billing={billing} onClose={onClose} />
      )}
    </>
  );
};
